import React from "react";
import { useParams } from "react-router-dom";
import { useGetQuizAttemptDetailsAdminQuery } from "../../slices/quizAttemptApiSlice";

const QuizAttemptDetails = () => {
  const { attemptId } = useParams();

  const {
    data: attempt,
    isLoading,
    isError,
    error,
  } = useGetQuizAttemptDetailsAdminQuery(attemptId);

  console.log(attempt);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[300px]">
        <p className="text-gray-500">Loading attempt details...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-red-50 text-red-600 border border-red-200 p-4 rounded-lg">
        {error?.data?.message || "Unable to load attempt"}
      </div>
    );
  }

  if (!attempt) {
    return (
      <div className="text-center text-gray-500">
        No attempt found.
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">
          Quiz Attempt Details
        </h1>
        <p className="text-gray-500 mt-1">
          Detailed student performance report
        </p>
      </div>

      {/* Student Details */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border shadow-sm p-5">
          <h2 className="font-semibold mb-4">Student Details</h2>

          <p>
            <strong>Name:</strong> {attempt.student?.name}
          </p>

          <p>
            <strong>Student ID:</strong> {attempt.student?.studentId}
          </p>

          <p>
            <strong>Phone:</strong> {attempt.student?.phone}
          </p>
        </div>

        <div className="bg-white rounded-xl border shadow-sm p-5">
          <h2 className="font-semibold mb-4">Quiz Details</h2>

          <p>
            <strong>Quiz:</strong> {attempt.quiz?.title}
          </p>

          <p>
            <strong>Attempt:</strong> {attempt.attemptNumber}
          </p>

          <p>
            <strong>Status:</strong> {attempt.status}
          </p>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <SummaryCard title="Total" value={attempt.totalQuestions} />
        <SummaryCard
          title="Correct"
          value={attempt.correctAnswers}
          color="text-green-600"
        />
        <SummaryCard
          title="Wrong"
          value={attempt.wrongAnswers}
          color="text-red-600"
        />
        <SummaryCard
          title="Marks"
          value={`${attempt.obtainedMarks}/${attempt.totalMarks}`}
        />
        <SummaryCard
          title="Percentage"
          value={`${attempt.percentage}%`}
        />
      </div>

      {/* Questions */}
      <div className="bg-white rounded-xl border shadow-sm p-5">
        <h2 className="text-xl font-semibold mb-5">
          Question Analysis
        </h2>

        <div className="space-y-5">
          {attempt.questions?.map((question, index) => {
            const answer = attempt.answers?.find(
              (item) => item.question?.toString() === question._id?.toString()
            );

            return (
              <div
                key={question._id}
                className="border rounded-lg p-5"
              >
                <div className="flex justify-between mb-3">
                  <h3 className="font-medium">
                    Q{index + 1}. {question.questionText}
                  </h3>

                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      answer?.isCorrect
                        ? "bg-green-100 text-green-700"
                        : "bg-red-100 text-red-700"
                    }`}
                  >
                    {answer?.isCorrect ? "Correct" : "Wrong"}
                  </span>
                </div>

                <div className="space-y-2">
                  {question.options?.map((option, optionIndex) => {
                    const optionText =
                      typeof option === "object"
                        ? option.text
                        : option;

                    const selectedAnswer =
                      typeof answer?.selectedAnswer === "object"
                        ? answer.selectedAnswer?.text
                        : answer?.selectedAnswer;

                    const correctAnswer =
                      typeof question.correctAnswer === "object"
                        ? question.correctAnswer?.text
                        : question.correctAnswer;

                    return (
                      <div
                        key={option._id || optionIndex}
                        className={`p-3 rounded border
                          ${
                            optionText === selectedAnswer
                              ? "border-blue-500 bg-blue-50"
                              : ""
                          }
                          ${
                            optionText === correctAnswer
                              ? "border-green-500 bg-green-50"
                              : ""
                          }
                        `}
                      >
                        {typeof option === "object"
                          ? `${option.key}. ${option.text}`
                          : option}
                      </div>
                    );
                  })}
                </div>

                <div className="mt-4 text-sm space-y-1">
                  <p>
                    <strong>Selected Answer:</strong>{" "}
                    {typeof answer?.selectedAnswer === "object"
                      ? answer.selectedAnswer?.text
                      : answer?.selectedAnswer || "Not answered"}
                  </p>

                  <p>
                    <strong>Correct Answer:</strong>{" "}
                    {typeof question.correctAnswer === "object"
                      ? question.correctAnswer?.text
                      : question.correctAnswer}
                  </p>

                  <p>
                    <strong>Marks:</strong>{" "}
                    {answer?.marksObtained || 0}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const SummaryCard = ({ title, value, color = "text-gray-800" }) => (
  <div className="bg-white border rounded-xl shadow-sm p-4 text-center">
    <p className="text-sm text-gray-500">{title}</p>
    <p className={`text-xl font-bold mt-2 ${color}`}>{value}</p>
  </div>
);

export default QuizAttemptDetails;